import { ExtendEstimateWithContext } from '../closeoutTypes';

// Type for row data structure
export interface RowData {
  type: 'header' | 'row';
  key: string;
  id: string;
  label?: string;
  data?: ExtendEstimateWithContext;
}

// Type for aggregated extension item
export interface AggregatedExtensionItem extends ExtendEstimateWithContext {
  aggregatedExtensions: ExtendEstimateWithContext[];
  _breakoutContext?: string;
}

/**
 * Creates a unique aggregation key that prevents items with different override statuses from being aggregated
 * When forceAggregateByName is true, only the item name is used (ignoring override status)
 */
const createAggregationKey = (
  item: ExtendEstimateWithContext,
  forceAggregateByName: boolean = false
): string => {
  const baseName = item.itemname || 'Unknown';

  if (forceAggregateByName) {
    // Force aggregation by name only - this allows setting overrides on aggregated rows
    return baseName;
  }

  // Create a key that includes override status to prevent aggregation of items with different override states
  const overrideStatus = [
    item.isoverrideitemunitcost ? 'UCO' : 'UC',
    item.isoverridehourlyproductionrate ? 'PRO' : 'PR',
  ].join('-');

  return `${baseName}|${overrideStatus}`;
};

/**
 * Aggregates extension items by name and override status, summing quantities, costs, and labor hours
 * Items with different override statuses will NOT be aggregated together unless forceAggregateByName is true
 */
export const aggregateItemsByName = (
  items: ExtendEstimateWithContext[],
  forceAggregateByName: boolean = false
): AggregatedExtensionItem[] => {
  const aggregatedMap = new Map<string, AggregatedExtensionItem>();

  items.forEach(item => {
    const key = createAggregationKey(item, forceAggregateByName);

    if (aggregatedMap.has(key)) {
      const existing = aggregatedMap.get(key)!;

      // Add this item to the aggregated extensions list
      existing.aggregatedExtensions.push(item);

      // Sum quantities
      const newTotalQty = (existing.totalitemqty || 0) + (item.totalitemqty || 0);
      const newTotalCost = (existing.totalcost || 0) + (item.totalcost || 0);
      const newTotalLabor =
        (existing.totaldirectlaborhours || 0) + (item.totaldirectlaborhours || 0);

      // Update totals
      existing.totalitemqty = newTotalQty;
      existing.totalcost = newTotalCost;
      existing.totaldirectlaborhours = newTotalLabor;

      // Handle unit cost calculation based on override status
      if (existing.isoverrideitemunitcost) {
        // For override items, calculate weighted average of override values, not total cost
        let totalOverrideValue = 0;
        let totalOverrideQty = 0;

        existing.aggregatedExtensions.forEach(aggItem => {
          const qty = (aggItem.totalitemqty || 0) * (aggItem.itemuomvalue || 1);
          totalOverrideValue += (aggItem.overrideitemunitcost || 0) * qty;
          totalOverrideQty += qty;
        });

        existing.overrideitemunitcost =
          totalOverrideQty > 0 ? totalOverrideValue / totalOverrideQty : 0;
        // For display purposes, also update itemunitcost to match
        existing.itemunitcost = existing.overrideitemunitcost;
      } else {
        // For non-override items, calculate weighted average from actual unit costs
        let totalUnitCostValue = 0;
        let totalUnitCostQty = 0;

        existing.aggregatedExtensions.forEach(aggItem => {
          const qty = (aggItem.totalitemqty || 0) * (aggItem.itemuomvalue || 1);
          totalUnitCostValue += (aggItem.itemunitcost || 0) * qty;
          totalUnitCostQty += qty;
        });

        existing.itemunitcost = totalUnitCostQty > 0 ? totalUnitCostValue / totalUnitCostQty : 0;
      }

      // Handle production rate calculation based on override status
      if (existing.isoverridehourlyproductionrate) {
        // For override items, calculate weighted average of override values
        let totalOverrideRate = 0;
        let totalOverrideQty = 0;

        existing.aggregatedExtensions.forEach(aggItem => {
          const qty = (aggItem.totalitemqty || 0) * (aggItem.itemuomvalue || 1);
          totalOverrideRate += (aggItem.overridehourlyproductionrate || 0) * qty;
          totalOverrideQty += qty;
        });

        existing.overridehourlyproductionrate =
          totalOverrideQty > 0 ? totalOverrideRate / totalOverrideQty : 0;
        // For display purposes, also update hourlyproductionrate to match
        existing.hourlyproductionrate = existing.overridehourlyproductionrate;
      } else {
        // For non-override items, calculate weighted average from actual production rates
        let totalProductionRateValue = 0;
        let totalProductionRateQty = 0;

        existing.aggregatedExtensions.forEach(aggItem => {
          const qty = (aggItem.totalitemqty || 0) * (aggItem.itemuomvalue || 1);
          totalProductionRateValue += (aggItem.hourlyproductionrate || 0) * qty;
          totalProductionRateQty += qty;
        });

        existing.hourlyproductionrate =
          totalProductionRateQty > 0 ? totalProductionRateValue / totalProductionRateQty : 0;
      }

      // Aggregate other properties that might be useful
      existing.isquoted = existing.isquoted || item.isquoted;
    } else {
      // Create new aggregated item
      const aggregatedItem: AggregatedExtensionItem = {
        ...item,
        aggregatedExtensions: [item],
      };
      aggregatedMap.set(key, aggregatedItem);
    }
  });

  return Array.from(aggregatedMap.values());
};

/**
 * Groups extension items by breakout, then aggregates by name within each breakout
 * Used for detailed breakout-based aggregation
 */
export const aggregateItemsByBreakoutAndName = (
  items: ExtendEstimateWithContext[]
): Record<string, { breakoutName: string; items: AggregatedExtensionItem[] }> => {
  // First group by breakout
  const breakoutGroups = items.reduce(
    (acc, item) => {
      const key = item.breakoutid || 'No Breakout';
      if (!acc[key]) {
        acc[key] = {
          breakoutName: item.breakoutname || `Breakout ID: ${key}`,
          items: [],
        };
      }
      acc[key].items.push(item);
      return acc;
    },
    {} as Record<string, { breakoutName: string; items: ExtendEstimateWithContext[] }>
  );

  // Then aggregate by name within each breakout
  const result: Record<string, { breakoutName: string; items: AggregatedExtensionItem[] }> = {};

  Object.entries(breakoutGroups).forEach(([breakoutKey, group]) => {
    const aggregatedItems = aggregateItemsByNameWithinGroup(group.items, breakoutKey);
    result[breakoutKey] = {
      breakoutName: group.breakoutName,
      items: aggregatedItems,
    };
  });

  return result;
};

/**
 * Aggregates items by name within a specific group (used for attribute groups or breakouts)
 * Maintains the breakout context for each aggregated item
 * Uses the same aggregation logic as aggregateItemsByName to ensure consistency
 */
export const aggregateItemsByNameWithinGroup = (
  items: ExtendEstimateWithContext[],
  breakoutContext?: string,
  forceAggregateByName: boolean = false
): AggregatedExtensionItem[] => {
  // Use the same aggregation logic as aggregateItemsByName to ensure consistency
  const aggregatedItems = aggregateItemsByName(items, forceAggregateByName);

  // Add breakout context to each aggregated item
  return aggregatedItems.map(item => ({
    ...item,
    _breakoutContext: breakoutContext,
  }));
};

/**
 * Creates flattened rows for display, with optional breakout headers
 * This function matches the logic used in ExtentionHeader.tsx
 */
export const createFlattenedRows = (
  items: ExtendEstimateWithContext[],
  isBreakoutMode: boolean = false
): RowData[] => {
  const rows: RowData[] = [];

  if (isBreakoutMode) {
    // Breakout mode - group by breakout with headers
    const groupedItems = aggregateItemsByBreakoutAndName(items);

    Object.keys(groupedItems)
      .sort()
      .forEach(breakoutKey => {
        const group = groupedItems[breakoutKey];

        // Insert a header row for the breakout
        rows.push({
          type: 'header',
          key: `header-${breakoutKey}`,
          id: breakoutKey,
          label: group.breakoutName,
        });

        // Add aggregated items for this breakout
        group.items.forEach((aggregatedItem, index) => {
          rows.push({
            type: 'row',
            key: `row-${aggregatedItem.itemid}-${aggregatedItem.attributegroupname}-${breakoutKey}-${index}`,
            id: breakoutKey,
            data: aggregatedItem,
          });
        });
      });
  } else {
    // Simple mode - aggregate by name only, ignoring override status differences
    // This allows setting overrides on aggregated rows that will apply to all underlying items
    const aggregatedItems = aggregateItemsByName(items, true); // forceAggregateByName = true

    aggregatedItems.forEach((aggregatedItem, index) => {
      rows.push({
        type: 'row',
        key: `row-${aggregatedItem.itemid}-${aggregatedItem.itemname}-${index}`,
        id: aggregatedItem.itemid,
        data: aggregatedItem,
      });
    });
  }

  return rows;
};

/**
 * Gets the material breakdown for reports, with optional aggregation
 * This is used by the ChangeOrderReport component
 */
export const getMaterialBreakdownForReport = (
  extensionItems: ExtendEstimateWithContext[],
  aggregateWithoutBreakouts: boolean = true
): Array<{
  id: string;
  description: string;
  breakout: string;
  quantity: number;
  unit: string;
  unitCost: number;
  totalMaterialCost: number;
  totalLaborHours: number;
  isQuoted: boolean;
  aggregatedCount?: number; // How many individual items were aggregated
}> => {
  if (!extensionItems || extensionItems.length === 0) return [];

  let processedItems: AggregatedExtensionItem[];

  if (aggregateWithoutBreakouts) {
    // Simple aggregation by name only
    processedItems = aggregateItemsByName(extensionItems, true); // forceAggregateByName = true
  } else {
    // Group by breakout, then by name within each breakout
    const breakoutGroups = aggregateItemsByBreakoutAndName(extensionItems);
    processedItems = Object.values(breakoutGroups).flatMap(group => group.items);
  }

  return processedItems
    .filter(item => item.totalcost > 0 || item.totaldirectlaborhours > 0)
    .map(item => ({
      id: item.itemid || '',
      description: item.itemname || 'Unknown Item',
      breakout: aggregateWithoutBreakouts
        ? `Multiple (${item.aggregatedExtensions.length} items)`
        : item.breakoutname || 'General',
      quantity: item.totalitemqty || 0,
      unit: item.itemuomname || 'EA',
      unitCost: item.itemunitcost || 0,
      totalMaterialCost: item.totalcost || 0,
      totalLaborHours: item.totaldirectlaborhours || 0,
      isQuoted: item.isquoted || false,
      aggregatedCount: item.aggregatedExtensions.length,
    }));
};
